import { trpc, trpcApi } from '@bitbot/api';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  authCodeReg,
  emailCodeReg,
  emailReg,
} from '@bitbot/shared/utils/regex';
import { debounce } from 'lodash';
import { Notify } from '@bitbot/ui-design';
import { useWallets } from '../wallet';
import { useProfile } from './profile';
import { sendGAEvent } from '@next/third-parties/google';

export const useEmail = () => {
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [countDown, setCountDown] = useState(360);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [active, setActive] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [otpCodeError, setOtpCodeError] = useState(false);
  const [emailCodeError, setEmailCodeError] = useState(false);
  const infoRef = useRef<{
    email?: string;
    emailCode?: string;
    authCode?: string;
  }>();

  useEffect(() => {
    setOtpCodeError(false);
  }, [authCode]);

  useEffect(() => {
    setEmailCodeError(false);
  }, [emailCode]);

  useEffect(() => {
    infoRef.current = {
      email,
      authCode,
      emailCode,
    };
  }, [email, authCode, emailCode]);

  const status = useMemo(() => {
    const emailStatus = emailReg.test(email);
    const emailCodeStatus = emailCodeReg.test(emailCode);
    const authCodeStatus = authCodeReg.test(authCode);
    return {
      emailStatus,
      emailCodeStatus,
      authCodeStatus,
      status: emailStatus && emailCodeStatus && authCodeStatus,
    };
  }, [email, emailCode, authCode]);

  useEffect(() => {
    if (hasSent) {
      const t = setTimeout(() => {
        if (countDown > 1) {
          setCountDown(countDown - 1);
        } else {
          setHasSent(false);
          setCountDown(10);
        }
      }, 1000);
    }
  }, [hasSent, countDown]);

  return {
    email,
    setEmail,
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    authCode,
    setAuthCode,
    active,
    setActive,
    showStatus,
    setShowStatus,
    isSuccess,
    setIsSuccess,
    status,
    isSendingCode,
    setIsSendingCode,
    otpCodeError,
    setOtpCodeError,
    emailCodeError,
    setEmailCodeError,
    infoRef,
  };
};

export const useAddEmail = () => {
  const {
    email,
    setEmail,
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    authCode,
    setAuthCode,
    active,
    setActive,
    showStatus,
    setShowStatus,
    status,
    setIsSendingCode,
    isSendingCode,
    otpCodeError,
    setOtpCodeError,
    setEmailCodeError,
    emailCodeError,
  } = useEmail();

  const { getWallets } = useWallets();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const sendCode = useCallback(
    debounce(() => {
      if (hasSent) return;
      setIsSendingCode(true);
      trpcApi.user.registerEmail
        .query({ email })
        .then(data => {
          if (data) {
            setHasSent(true);
          } else {
            Notify.error('Send email code failed');
          }
        })
        .catch(e => {
          Notify.error(e.message);
        })
        .finally(() => {
          setIsSendingCode(false);
        });
    }, 300),
    [hasSent, email],
  );
  const verifyEmail = useCallback(
    debounce(() => {
      setIsLoading(true);
      trpcApi.user.verifyEmail
        .query({
          code: emailCode,
          otp: authCode,
          email,
        })
        .then(data => {
          if (data) {
            getWallets(true);
            setIsSuccess(true);
            sendGAEvent('event', 'set_email_success');
          } else {
            setIsSuccess(false);
          }
          setShowStatus(true);
        })
        .catch(e => {
          if (e.message === 'Invalid OTP') {
            setOtpCodeError(true);
          } else if (e.message === 'Invalid or expired code') {
            setEmailCodeError(true);
            setActive(false);
          } else {
            Notify.error(e.message);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 300),
    [emailCode, authCode, email],
  );

  return {
    email,
    setEmail,
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    status,
    authCode,
    setAuthCode,
    active,
    setActive,
    sendCode,
    showStatus,
    setShowStatus,
    isSuccess,
    verifyEmail,
    isSendingCode,
    isLoading,
    otpCodeError,
    setOtpCodeError,
    setEmailCodeError,
    emailCodeError,
  };
};

export const useChangeEmail = () => {
  const {
    email,
    setEmail,
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    authCode,
    setAuthCode,
    active,
    setActive,
    showStatus,
    setShowStatus,
    isSuccess,
    status,
    setIsSuccess,
    setIsSendingCode,
    isSendingCode,
    otpCodeError,
    setOtpCodeError,
    setEmailCodeError,
    emailCodeError,
  } = useEmail();
  const [isLoading, setIsLoading] = useState(false);
  const { email: prevEmail } = useProfile();
  const { getWallets } = useWallets();

  const sendCode = useCallback(
    debounce(() => {
      if (hasSent) return;
      setIsSendingCode(true);
      trpcApi.user.changeEmail
        .query({ newEmail: email, prevEmail: prevEmail! })
        .then(data => {
          if (data) {
            setHasSent(true);
          } else {
            Notify.error('Send email code failed');
          }
        })
        .catch(e => {
          Notify.error(e.message);
        })
        .finally(() => {
          setIsSendingCode(false);
        });
    }, 300),
    [hasSent, email, prevEmail],
  );
  const confirmChangeEmail = useCallback(
    debounce(() => {
      setIsLoading(true);
      trpcApi.user.confirmChangeEmail
        .query({
          code: emailCode,
          otp: authCode,
          email,
        })
        .then(data => {
          setShowStatus(true);
          if (data) {
            getWallets(true);
            setIsSuccess(true);
          } else {
            setIsSuccess(false);
            Notify.error('Change email failed');
          }
        })
        .catch(e => {
          if (e.message === 'Invalid OTP') {
            setOtpCodeError(true);
          } else if (e.message === 'Invalid or expired code') {
            setEmailCodeError(true);
            setActive(false);
          } else {
            Notify.error(e.message);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 300),
    [emailCode, authCode, email],
  );

  return {
    email,
    setEmail,
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    status,
    authCode,
    setAuthCode,
    active,
    setActive,
    sendCode,
    showStatus,
    setShowStatus,
    isSuccess,
    confirmChangeEmail,
    isSendingCode,
    isLoading,
    otpCodeError,
    setOtpCodeError,
    setEmailCodeError,
    emailCodeError,
  };
};

export const useChangeTwofa = () => {
  const [emailCode, setEmailCode] = useState('');
  const [countDown, setCountDown] = useState(360);
  const [hasSent, setHasSent] = useState(false);
  const [authCode, setAuthCode] = useState('');

  const status = useMemo(() => {
    const emailCodeStatus = emailCodeReg.test(emailCode);
    const authCodeStatus = authCodeReg.test(authCode);
    return {
      emailCodeStatus,
      authCodeStatus,
      status: emailCodeStatus && authCodeStatus,
    };
  }, [emailCode, authCode]);
  const sendCode = useCallback(
    debounce(() => {
      if (hasSent) return;
      setHasSent(true);
      // trpcApi.user.registerEmail();
    }, 300),
    [hasSent],
  );

  useEffect(() => {
    if (hasSent) {
      const t = setTimeout(() => {
        if (countDown > 1) {
          setCountDown(countDown - 1);
        } else {
          setHasSent(false);
          setCountDown(10);
        }
      }, 1000);
    }
  }, [hasSent, countDown]);
  return {
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    status,
    authCode,
    setAuthCode,
    sendCode,
  };
};
